import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { API, fileUrl } from "@/lib/api";
import { Search, ArrowUpRight } from "lucide-react";

export default function PublicSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      axios
        .get(`${API}/public/search`, { params: { q } })
        .then((r) => setItems(r.data.results || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <PublicLayout hideSearch>
      <section className="max-w-5xl mx-auto px-4 md:px-10 py-10 md:py-20">
        <div className="hed-overline">Catalog · search</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight mt-3 max-w-2xl leading-[1.05]">
          Find the right plywood, by name, code, or where you&rsquo;ll use it.
        </h1>

        <div className="mt-8 md:mt-10 flex items-center gap-3 border-b-2 border-[#1A1A18] focus-within:border-[#B5953B] py-3">
          <Search size={20} strokeWidth={1.5} className="text-[#5C5C54]" />
          <input
            data-testid="public-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'marine ply', 'ST4738', 'kitchen'..."
            className="flex-1 bg-transparent text-base md:text-lg outline-none placeholder-[#A3A39A]"
            autoFocus
          />
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="text-sm text-[#5C5C54]">Searching...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[#5C5C54]">
              {q ? "No matching products found." : "No published products yet."}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 hed-stagger">
              {items.map((p) => (
                <Link
                  to={`/product/${p.code}`}
                  key={p.id}
                  className="hed-card overflow-hidden group"
                  data-testid={`search-result-${p.code}`}
                >
                  <div className="aspect-[4/3] bg-[#F2F0EA] overflow-hidden">
                    {p.images?.[0] ? (
                      <img
                        src={fileUrl(p.images[0].storage_path)}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl font-serif text-[#5E6033]">
                        {(p.name || "H")[0]}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="hed-overline">{p.category || "Product"}</span>
                      <ArrowUpRight size={16} strokeWidth={1.5} className="text-[#5C5C54] group-hover:text-[#B5953B] transition-colors" />
                    </div>
                    <h3 className="font-serif text-xl mt-2 leading-tight">{p.name}</h3>
                    <div className="text-xs text-[#5C5C54] font-mono mt-1">{p.code}</div>
                    {p.material && <div className="text-xs text-[#5C5C54] mt-3">{p.material}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
